import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ISSUER_MESSAGES } from 'appRootPath/messages';
import { AuctionObject, BidNegotiation } from 'appRootPath/models/commercial-papers.model';
import { AuctionService } from 'appRootPath/services/auction.service';
import { BID_ACTIONS } from 'appRootPath/shared/auction-metadata';
import {
  getDateFromCalendarObj, getTransformedDateTime,
  getTransformedNumber, getTransformedDate, getDiffDaysFromBaseDate,
  getAmountObject, getCalendarDateObj, isWeekendAndHolidayDisabled,
  getCalendarTimeObj, getMomentFromCalendarDateTime
} from 'appRootPath/utils';
import {
  getNewAmountFg,
  HolidayValidator,
  maxDaysFromTodayValidator,
  tenureHolidayWeekendValidator
} from 'appRootPath/validationUtils';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import {
  API_DATE_FORMAT, TENURE_RANGE, VALUE_DATE_MAX
} from '../../global-meta-data';

@Component({
  selector: 'app-bid-form',
  templateUrl: './bid-form.component.html',
  styleUrls: ['./bid-form.component.scss']
})
export class BidFormComponent implements OnInit {
  @Input() formAction;
  @Input() isInvestor: boolean;
  @Input() auctionDetails: AuctionObject;
  @Input() bidDetails: BidNegotiation;
  @Output() bidFormSubmitEvent = new EventEmitter<BidNegotiation>();

  bidFormGroup: FormGroup;
  actionConfigMap;

  minTenure = TENURE_RANGE.min;
  maxTenure = TENURE_RANGE.max;
  isWeekendAndHolidayDisabled = isWeekendAndHolidayDisabled;
  today = getCalendarDateObj(new Date());
  valueDateMax = getCalendarDateObj(moment().add(VALUE_DATE_MAX, 'days').toDate());

  // tenureDate - calculate maturity date from tenure

  constructor(
    private bidFB: FormBuilder,
    private auctionService: AuctionService,
    private toastr: ToastrService
  ) {
    this.actionConfigMap = this.auctionService.roleBiddingConfig.actionConfigMap;
  }

  /**
   * Angular Init Lifrcycle hook
   * Initializes bid form and prefills data based on the form action
   */
  ngOnInit(): void {
    this.initBidForm();
    this.prefillBidForm(this.formAction);
  }

  /**
   * Initialises the bidding form.
   */
  initBidForm(): void {
    this.bidFormGroup = this.bidFB.group({
      amount: getNewAmountFg(), // add validation for exceedsAuctionValue
      roi: [0, Validators.compose([Validators.required, Validators.min(0.01)])],
      is_cbc: false,
      value_date: ['', [HolidayValidator, maxDaysFromTodayValidator, Validators.required]],
      tenure: [TENURE_RANGE.min, Validators.compose([
        Validators.required,
        Validators.min(TENURE_RANGE.min),
        Validators.max(TENURE_RANGE.max)])
      ],
      expiryTime: [{ hour: 23, minute: 59 }, Validators.compose([Validators.required])],
      // validation should not be past date
      expiryDate: [null, Validators.compose([Validators.required])]
    }, {
      validator: (bidForm) => {
        const tenureErrors = tenureHolidayWeekendValidator(bidForm);
        const expiryErrors = this.bidExpiryLessThanAuctionExpiry(bidForm, this.auctionDetails.auction_expiry);
        const errors = Object.assign({}, tenureErrors, expiryErrors);
        return Object.keys(errors).length > 0 ? errors : null;
      }
    });
  }

  /**
   * Check if bid expiry is 5 seconds less than auction expiry
   */
  bidExpiryLessThanAuctionExpiry(bidForm: FormGroup, auctionExpiryDate) {
    if (!auctionExpiryDate) {
      return {};
    }

    const bidExpiry = getMomentFromCalendarDateTime(
      bidForm.get('expiryDate').value,
      bidForm.get('expiryTime').value);

    const expiryLess5sec = moment(auctionExpiryDate).subtract(5, 'seconds');

    return bidExpiry.isSameOrAfter(expiryLess5sec) ? {
      bidExpiry: { moreThanAuctionExpiry: true }
    } : {};
  }

  /**
   * Handles prefilling of the bid form with the auction details or previous bid details.
   * Disabled tenure and value date if those values are frozen.
   * @param action - Bid Action value.
   */
  prefillBidForm(action: string) {
    if (action === BID_ACTIONS.PLACE_BID && this.auctionDetails) {
      this.handlePreFillFromAuction(this.auctionDetails);
    } else if (this.bidDetails) {
      this.handlePrefillFromPreviousBid(this.bidDetails);
    }

    if (this.auctionDetails.maturity_date_frozen) {
      this.bidFormGroup.get('tenure').disable();
    }

    if (this.auctionDetails.value_date_frozen) {
      this.bidFormGroup.get('value_date').disable();
    }
  }

  /**
   * Handles prefilling of bid form from auction details.
   * @param auctionData - Auction Details.
   */
  handlePreFillFromAuction(auctionData: AuctionObject) {

    const auctionExpiryToSet = moment(auctionData.auction_expiry).subtract(6, 'seconds');

    this.bidFormGroup.patchValue({
      amount: getAmountObject(auctionData.tentative_cap),
      roi: auctionData.tentative_rate,
      value_date: getCalendarDateObj(moment(auctionData.tentative_value_date).toDate()),
      expiryDate: getCalendarDateObj(auctionExpiryToSet.toDate()),
      expiryTime: getCalendarTimeObj(auctionExpiryToSet.toDate()),
      tenure: getDiffDaysFromBaseDate(
        moment(auctionData.tentative_value_date),
        moment(auctionData.tentative_maturity_date),
        false
      )
    });
  }

  /**
   * Handles prefilling of the bid form from previous bid details.
   * @param bidData - Details of the bid.
   */
  handlePrefillFromPreviousBid(bidData: BidNegotiation) {
    const bidExpiry = moment(bidData.bid_expiry);
    this.bidFormGroup.patchValue({
      amount: getAmountObject(bidData.amount),
      roi: bidData.roi,
      is_cbc: false,
      value_date: getCalendarDateObj(moment(bidData.value_date).toDate()),
      expiryDate: getCalendarDateObj(bidExpiry.toDate()),
      expiryTime: getCalendarTimeObj(bidExpiry.toDate()),
      tenure: getDiffDaysFromBaseDate(
        moment(bidData.value_date),
        moment(bidData.maturity_date),
        false
      )
    });
  }

  /**
   * Handle counter actions here.
   */
  handleBidAction(bidFormValue) {
    if (this.bidFormGroup.invalid) {
      this.toastr.warning(ISSUER_MESSAGES.INVALID_FORM);
      return;
    }
    let bidFormData;
    bidFormData = Object.assign({}, bidFormValue, {
      amount: getTransformedNumber(bidFormValue.amount.amount, bidFormValue.amount.amountIn),
      bid_expiry: getTransformedDateTime(bidFormValue.expiryDate, bidFormValue.expiryTime, false),
      maturity_date: this.auctionDetails.maturity_date_frozen ||
        getDateFromCalendarObj(bidFormValue.value_date)
          .add(bidFormValue.tenure, 'days').format(API_DATE_FORMAT),
      value_date: this.auctionDetails.value_date_frozen ||
        getTransformedDate(bidFormValue.value_date, null, true)
    });

    delete bidFormData.expiryTime;
    delete bidFormData.expiryDate;
    this.bidFormSubmitEvent.emit(bidFormData);
  }
}
